import {Box, CircularProgress, IconButton, Stack, Typography} from "@mui/joy";
import {ArrowLeft, Eraser} from "lucide-react";
import Card from "../components/Card";
import Button from "../components/Button";
import {useEffect, useRef, useState} from "react";
import TeamSearchField from "../components/TeamSearchField";
import {TeamPreview} from "../types/team";
import Events from "../components/league/Events";
import {useTeamEvents} from "../hooks/useTeamEvents";
import {useAnalyze} from "../hooks/useAnalyze";
import {useApiService} from "../hooks/useApiService";
import MatchAnalysis from "../components/MatchAnalysis";
import AnalyzeProgress from "../components/AnalyzeProgress";
import {findTeamByName, sameTeamName} from "../services/eventSearch";
import {Event} from "../types/event";
import {useTranslation} from "react-i18next";
import SubscriptionNotice from "../components/SubscriptionNotice";
import PageContainer from "../components/layout/PageContainer";

const Matches = () => {
    const [homeTeam, setHomeTeam] = useState<TeamPreview>()
    const [awayTeam, setAwayTeam] = useState<TeamPreview>()
    const teamEvents = useTeamEvents(homeTeam, awayTeam)
    const analyze = useAnalyze()
    const apiService = useApiService()
    const {t} = useTranslation()
    /**
     * Set when a pick is made by the page itself, filling in the sides of a fixture the visitor has
     * just picked: that analysis is the one being started and must not be reset by its own picks.
     */
    const keepAnalysisRef = useRef(false)
    /**
     * Bumped to start the search over.
     *
     * The fields hold the text that was typed in them, so clearing the picks is not enough: keying
     * them on this hands back a pair of empty fields, whether or not what was typed was ever picked.
     */
    const [searchKey, setSearchKey] = useState(0)

    /** Every call gets its own client because the api services write their params on client.defaults */
    const newClient = () => apiService.client(true, false)

    useEffect(() => {
        if (keepAnalysisRef.current) {
            keepAnalysisRef.current = false
            return
        }
        // The previous analysis no longer describes the selected pair
        if (homeTeam || awayTeam) analyze.reset()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeTeam?.id, awayTeam?.id])

    /**
     * Analyzes a fixture picked from the list, and fills the search fields with the sides it is
     * played between.
     *
     * The prediction api names its teams in full but carries no crest, so a side that is not already
     * picked is looked up by that full name against the team search, which is where the logos shown
     * on this page and in the analysis panel come from.
     */
    const selectEvent = async (event: Event) => {
        const analysis = analyze.analyze({name: event.team_home?.name}, {name: event.team_away?.name})

        if (homeTeam && awayTeam) return analysis

        const picked = [homeTeam, awayTeam].filter(Boolean)
        const isPicked = (name?: string) => picked.some(team => sameTeamName(team.name, name))

        try {
            const found = await Promise.all([event.team_home?.name, event.team_away?.name]
                .map(name => !name || isPicked(name) ? undefined : findTeamByName(newClient, name)))

            /**
             * A side already picked can sit in either field, so the sides that are left fill whatever
             * field is still empty rather than the one they are played on. The ids weed out a club the
             * two apis name differently enough for the name comparison to have missed it.
             */
            const pickedIds = picked.map(team => team.id)
            const remaining = found.filter(team => team && !pickedIds.includes(team.id))

            const nextHome = homeTeam ? undefined : remaining.shift()
            const nextAway = awayTeam ? undefined : remaining.shift()

            if (nextHome || nextAway) {
                keepAnalysisRef.current = true
                if (nextHome) setHomeTeam(nextHome)
                if (nextAway) setAwayTeam(nextAway)
            }
        } catch (error) {
            // A crest is worth no more than that: the analysis carries on without it
        }

        return analysis
    }

    /** Empties both fields and whatever they had produced, back to the state of an untouched page */
    const clearSearch = () => {
        setHomeTeam(undefined)
        setAwayTeam(undefined)
        setSearchKey(key => key + 1)
        analyze.reset()
    }

    /** The picked teams hold the crests, the analyzed event only names its sides */
    const logoOf = (name?: string) =>
        [homeTeam, awayTeam].find(team => team && sameTeamName(team.name, name))?.logo

    const pickedNames = [homeTeam, awayTeam].filter(Boolean).map(team => team.name)

    /**
     * The predictions take the whole page over.
     *
     * Reading them next to the fields that started them left the analysis competing for room with a
     * search that has already been made, so the search steps aside and the arrow brings it back.
     */
    const analysisShown = !!analyze.event

    const eventsTitle = pickedNames.length === 2
        ? t("upcomingMatchesBetween", {home: pickedNames[0], away: pickedNames[1]})
        : pickedNames.length === 1
            ? t("upcomingMatchesOf", {team: pickedNames[0]})
            : t("upcomingMatches")

    return <PageContainer width={{
        md: analyze.event ? "48rem" : "38rem",
        lg: analyze.event ? "68rem" : "52rem"
    }}>

        {analysisShown
            ? <Stack direction={"row"} alignItems={"center"} gap={1}>
                <IconButton variant={"outlined"} color={"neutral"} onClick={analyze.reset}
                            aria-label={t("backToTeamSearch")}>
                    <ArrowLeft size={18}/>
                </IconButton>
                <Typography level={"title-md"}>
                    {t("backToTeamSearch")}
                </Typography>
            </Stack>
            : <Stack alignItems={"center"} gap={1}>
                <Typography level={"h2"} textAlign={"center"} fontSize={{xs: "1.6rem", sm: undefined}}>
                    {t("matchAnalysisTitle")}
                </Typography>
                <Typography textAlign={"center"}>
                    {t("enterTeamsSubtitle")}
                </Typography>
                <Typography level={"body-xs"} textAlign={"center"}>
                    {t("aiNotice")}
                </Typography>
            </Stack>}

        {!analysisShown && <Card>
            <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} gap={2}>
                <Typography>
                    {t("matchToAnalyze")}
                </Typography>

                {/* Nothing picked is already a cleared search, so the button waits until there is one */}
                {!!pickedNames.length && <Button size={"sm"} variant={"soft"} color={"neutral"}
                                                 startDecorator={<Eraser size={16}/>}
                                                 onClick={clearSearch}
                                                 sx={{background: "rgba(255,255,255,0.06)", color: "text.tertiary"}}>
                    {t("clearSearch")}
                </Button>}
            </Stack>

            <Stack gap={2}>
                {homeTeam && <Box width={48} alignSelf={"center"} component={"img"} src={homeTeam.logo}/>}
                <TeamSearchField key={`home-${searchKey}`}
                                 placeholder={t("teamSearchPlaceholder")}
                                 selectedTeam={homeTeam}
                                 onSelect={setHomeTeam}/>
            </Stack>

            <Typography textAlign={"center"}>
                {t("versus")}
            </Typography>

            <Stack gap={2}>
                {awayTeam && <Box alignSelf={"center"} width={45} component={"img"} src={awayTeam.logo}/>}
                <TeamSearchField key={`away-${searchKey}`}
                                 placeholder={t("teamSearchPlaceholder")}
                                 selectedTeam={awayTeam}
                                 onSelect={setAwayTeam}/>
            </Stack>

            <Button size={"lg"}
                    loading={analyze.loading}
                    disabled={!homeTeam || !awayTeam}
                    onClick={() => analyze.analyze(
                        {name: homeTeam.name, teamId: homeTeam.id},
                        {name: awayTeam.name, teamId: awayTeam.id})}>
                {t("analyzeWithAi")}
            </Button>

            {analyze.loading && <AnalyzeProgress progress={analyze.progress} onCancel={analyze.cancel}/>}

            {analyze.error && <Typography level={"body-xs"} color={"danger"}>
                {analyze.error}
            </Typography>}

            {/* A refusal is not an error to retry: it is a plan to buy, and it says which one */}
            {analyze.denial && <SubscriptionNotice denial={analyze.denial}/>}
        </Card>}

        {analyze.event && <MatchAnalysis event={analyze.event}
                                         homeLogo={logoOf(analyze.event.team_home?.name)}
                                         awayLogo={logoOf(analyze.event.team_away?.name)}/>}

        <Card>
            <Typography>
                {eventsTitle}
            </Typography>

            {teamEvents.loading
                ? <Typography startDecorator={<CircularProgress size={"sm"}/>}>
                    {t("searchingUpcoming")}
                </Typography>
                : !pickedNames.length
                    ? <Typography level={"body-sm"}>
                        {t("pickTeamHint")}
                    </Typography>
                    : teamEvents.unmatched.length
                        ? <Typography level={"body-sm"}>
                            {t("notCoveredYet", {teams: teamEvents.unmatched.join(", ")})}
                        </Typography>
                        : teamEvents.events.length
                            ? <Events events={teamEvents.events}
                                      selectedEvent={analyze.event}
                                      onSelect={selectEvent}/>
                            : <Typography level={"body-sm"}>
                                {t("noUpcomingMatch")}
                            </Typography>}
        </Card>

    </PageContainer>
}

export default Matches
